import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { uploadPrivate } from '../lib/storage'
import PrivateImage from '../components/PrivateImage'
import { useAuth } from '../context/AuthContext'

const CATEGORIES = ['All','wool','linen','cotton','silk','cashmere','blend','synthetic','other']
const PATTERNS = ['solid','stripe','check','plaid','herringbone','windowpane','houndstooth','other']
const SEASONS = ['year-round','summer','winter','spring-fall']
const STOCK_STATUS = ['available','low','out_of_stock','discontinued']

// ── FABRICS LIST ─────────────────────────────────────────────────
export function Fabrics() {
  const [fabrics, setFabrics] = useState([])
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('All')
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const timer = setTimeout(fetchFabrics, 300)
    return () => clearTimeout(timer)
  }, [search, category])

  async function fetchFabrics() {
    setLoading(true)
    let q = supabase.from('fabrics').select('*').order('created_at', { ascending: false })
    if (search) q = q.or(`fabric_code.ilike.%${search}%,name.ilike.%${search}%,color.ilike.%${search}%,supplier.ilike.%${search}%`)
    if (category !== 'All') q = q.eq('category', category)
    const { data } = await q
    setFabrics(data || [])
    setLoading(false)
  }

  return (
    <div>
      <div className="page-header" style={{ justifyContent: 'space-between' }}>
        <h1 className="page-title">Fabrics <span style={{ fontSize: '14px', color: 'var(--gray-400)', fontWeight: '400' }}>({fabrics.length})</span></h1>
        <Link to="/fabrics/new" className="btn btn-primary btn-sm"><Plus size={16} /> Add</Link>
      </div>

      <div className="search-bar">
        <Search size={16} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search code, name, color, supplier…" />
      </div>

      <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', marginBottom: '12px', paddingBottom: '4px', scrollbarWidth: 'none' }}>
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setCategory(c)} style={{
            padding: '5px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer',
            fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap',
            background: category === c ? 'var(--brown-dark)' : 'var(--white)',
            color: category === c ? 'var(--gold)' : 'var(--gray-600)',
            boxShadow: 'var(--shadow)', flexShrink: 0,
            textTransform: 'capitalize'
          }}>{c}</button>
        ))}
      </div>

      {loading ? (
        <div className="empty-state"><p style={{ color: 'var(--gray-400)' }}>Loading…</p></div>
      ) : fabrics.length === 0 ? (
        <div className="empty-state card">
          <p className="empty-state-title">No fabrics yet</p>
          <p className="empty-state-sub">Add your first fabric to start tracking</p>
          <Link to="/fabrics/new" className="btn btn-primary" style={{ marginTop: '12px' }}><Plus size={16} /> Add Fabric</Link>
        </div>
      ) : (
        <div className="fabrics-grid">
          {fabrics.map(f => (
            <div key={f.id} className="fabric-card" onClick={() => navigate(`/fabrics/${f.id}`)}>
              <div className="fabric-swatch">
                {f.image_url ? (
                  <PrivateImage src={f.image_url} alt={f.name} />
                ) : (
                  <div style={{ width: '100%', height: '100%', background: f.color_hex || '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#6B7280', textTransform: 'capitalize' }}>{f.category}</span>
                  </div>
                )}
              </div>
              <div className="fabric-card-body">
                <div className="fabric-code">{f.fabric_code}</div>
                <div className="fabric-name">{f.name}</div>
                <div className="fabric-meta">{[f.color, f.supplier].filter(Boolean).join(' · ')}</div>
                <div style={{ marginTop: '6px' }}>
                  <span className={`badge ${f.stock_status === 'available' ? 'badge-green' : f.stock_status === 'low' ? 'badge-amber' : 'badge-red'}`} style={{ fontSize: '9px' }}>
                    {f.stock_status?.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ height: '16px' }} />
    </div>
  )
}

// ── NEW FABRIC ────────────────────────────────────────────────────
export function NewFabric() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [saving, setSaving] = useState(false)
  const [imageUrl, setImageUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const [form, setForm] = useState({
    name: '', supplier: '', supplier_code: '',
    category: 'wool', sub_category: '',
    color: '', color_hex: '',
    pattern: 'solid', weight: '',
    season: 'year-round', country_of_origin: '',
    price_per_meter: '', price_currency: 'THB',
    stock_status: 'available', notes: '',
  })

  function set(k, v) { setForm(p => ({ ...p, [k]: v })) }

  async function uploadSwatch(file) {
    if (!file) return
    setUploading(true)
    const ext = file.name.split('.').pop()
    const path = `swatches/${Date.now()}.${ext}`
    try {
      setImageUrl(await uploadPrivate('fabric-swatches', path, file))
    } catch (err) {
      alert('Upload failed: ' + err.message)
    }
    setUploading(false)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)

    // Generate fabric code: FAB-[CAT3]-[timestamp short]
    const prefix = form.category.substring(0, 3).toUpperCase()
    const seq = String(Date.now()).slice(-4)
    const fabric_code = `FAB-${prefix}-${seq}`

    const { data, error } = await supabase.from('fabrics').insert({
      ...form,
      fabric_code,
      image_url: imageUrl || null,
      price_per_meter: form.price_per_meter ? Number(form.price_per_meter) : null,
      added_by: profile?.id,
    }).select().single()

    setSaving(false)
    if (!error) navigate('/fabrics')
    else alert('Error: ' + error.message)
  }

  return (
    <div>
      <div className="page-header">
        <button className="page-header-back" onClick={() => navigate(-1)}>←</button>
        <h1 className="page-title">Add Fabric</h1>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Swatch photo */}
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Swatch Photo</div>
          {imageUrl ? (
            <div style={{ position: 'relative' }}>
              <PrivateImage src={imageUrl} alt="Swatch" style={{ width: '100%', height: '160px', objectFit: 'cover', borderRadius: '8px' }} />
              <button type="button" onClick={() => setImageUrl('')} style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', fontSize: '16px' }}>×</button>
            </div>
          ) : (
            <label className="photo-upload">
              <p className="photo-upload-text" style={{ fontSize: '15px', fontWeight: '500' }}>{uploading ? 'Uploading…' : '📷 Photo of fabric swatch'}</p>
              <p className="photo-upload-text" style={{ fontSize: '12px', marginTop: '4px', color: 'var(--gray-400)' }}>Tap to upload or take photo</p>
              <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => uploadSwatch(e.target.files[0])} disabled={uploading} />
            </label>
          )}
        </div>

        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Fabric Details</div>
          <div className="form-group">
            <label className="form-label">Name <span>*</span></label>
            <input className="form-input" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Midnight Navy Super 150s" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" value={form.category} onChange={e => set('category', e.target.value)}>
                {['wool','linen','cotton','silk','cashmere','blend','synthetic','other'].map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Sub-category</label>
              <input className="form-input" value={form.sub_category} onChange={e => set('sub_category', e.target.value)} placeholder="e.g. Super 150s, Tweed" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Color</label>
              <input className="form-input" value={form.color} onChange={e => set('color', e.target.value)} placeholder="e.g. Navy Blue" />
            </div>
            <div className="form-group">
              <label className="form-label">Pattern</label>
              <select className="form-select" value={form.pattern} onChange={e => set('pattern', e.target.value)}>
                {PATTERNS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="form-section-title">Supplier</div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Supplier</label>
              <input className="form-input" value={form.supplier} onChange={e => set('supplier', e.target.value)} placeholder="Supplier name" />
            </div>
            <div className="form-group">
              <label className="form-label">Supplier Code</label>
              <input className="form-input" value={form.supplier_code} onChange={e => set('supplier_code', e.target.value)} placeholder="Supplier's ref" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Weight</label>
              <input className="form-input" value={form.weight} onChange={e => set('weight', e.target.value)} placeholder="e.g. 280gsm" />
            </div>
            <div className="form-group">
              <label className="form-label">Season</label>
              <select className="form-select" value={form.season} onChange={e => set('season', e.target.value)}>
                {SEASONS.map(s => <option key={s} value={s}>{s.replace('-',' / ')}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Price / metre</label>
              <input className="form-input" type="number" step="0.01" value={form.price_per_meter} onChange={e => set('price_per_meter', e.target.value)} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label className="form-label">Currency</label>
              <select className="form-select" value={form.price_currency} onChange={e => set('price_currency', e.target.value)}>
                {['THB','USD','GBP','EUR','AUD','SGD'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Stock Status</label>
            <select className="form-select" value={form.stock_status} onChange={e => set('stock_status', e.target.value)}>
              {STOCK_STATUS.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional notes…" />
          </div>
        </div>

        <button type="submit" className="btn btn-primary btn-lg" disabled={saving} style={{ borderRadius: '12px', marginBottom: '8px' }}>
          {saving ? 'Saving…' : '✓ Add Fabric'}
        </button>
      </form>
    </div>
  )
}

export default Fabrics
